import { useTranslation } from 'react-i18next';
import type { SpecNextStep } from '../../../lib/api/spec-requirements.js';

export interface NextStepCardProps {
  /** 今いる工程とやること。読み込み中・失敗時は null */
  step: SpecNextStep | null;
}

/**
 * 案件が今どの工程にいて、そこで何をするか。
 *
 * **案件を開いたときにいちばん上で目に入る場所に置く。** 「次に何をすべきか」を毎回人が
 * 成果物とレビューを見比べて決めていると、案件ごとに判断がばらつく。判定は API（＝MCP と
 * 同じ関数）が持ち、画面は並べるだけ。
 *
 * `runbook` は MCP のツール呼び出しとスキル名の列なので、人が画面でそのまま実行するものでは
 * ない。「Claude に頼むときの手順」として出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の要件定義が今いる工程と、そこでやること 1 つ・理由・待ち・他の宿題を出す
 * @graph-connects none
 */
export function NextStepCard({ step }: NextStepCardProps) {
  const { t } = useTranslation();

  if (step === null) {
    return (
      <p className="mt-6 text-sm text-[var(--color-text-muted)]">{t('spec.nextStep.loading')}</p>
    );
  }

  return (
    <section className="mt-6 border border-[var(--color-accent-primary)] bg-[var(--color-accent-muted)] rounded-lg p-4">
      <h2 className="text-xs font-semibold text-[var(--color-accent-primary)]">
        {t('spec.nextStep.heading')}
      </h2>
      <p className="mt-1 text-base font-semibold text-[var(--color-text-primary)]">{step.phase}</p>
      <p className="mt-1 text-sm text-[var(--color-text-primary)]">{step.action}</p>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{step.reason}</p>

      {step.waitingFor === '' ? null : (
        <p className="mt-2 text-sm text-[var(--color-warning-text)]">
          <span className="text-xs font-semibold">{t('spec.nextStep.waiting')} </span>
          {step.waitingFor}
        </p>
      )}

      {step.pending.length > 0 ? (
        <div className="mt-2">
          <h3 className="text-xs font-semibold text-[var(--color-accent-primary)]">
            {t('spec.nextStep.pending')}
          </h3>
          <ul className="mt-1 text-sm list-disc pl-5 text-[var(--color-text-primary)]">
            {step.pending.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {step.runbook.length > 0 ? (
        <details className="mt-3">
          <summary className="text-xs text-[var(--color-text-secondary)] cursor-pointer">
            {t('spec.nextStep.runbook')}
          </summary>
          <ol className="mt-1 text-xs list-decimal pl-5 text-[var(--color-text-secondary)] font-mono">
            {step.runbook.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ol>
        </details>
      ) : null}
    </section>
  );
}
